import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Briefcase, MessageSquare, User, Phone } from "lucide-react";

const BottomNav = () => {
  const [activeSection, setActiveSection] = useState("hero");
  const [isVisible, setIsVisible] = useState(false);

  const navItems = [
    { id: "hero", label: "Home", icon: Home },
    { id: "services", label: "Services", icon: Briefcase },
    { id: "testimonials", label: "Reviews", icon: MessageSquare },
    { id: "about", label: "About", icon: User },
    { id: "contact", label: "Contact", icon: Phone },
  ];

  useEffect(() => {
    const handleScroll = () => {
      // Show nav after scrolling 300px
      if (window.scrollY > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }

      // Update active section based on scroll position
      const sections = navItems.map((item) => document.getElementById(item.id));
      const scrollPosition = window.scrollY + window.innerHeight / 2;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(navItems[i].id);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Check initial state

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 0; // No header offset needed
      const elementPosition = element.offsetTop - offset;
      
      window.scrollTo({
        top: elementPosition,
        behavior: "smooth",
      });
      setActiveSection(id);
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }}
          className="fixed bottom-4 left-0 right-0 z-50 flex justify-center px-4"
        >
          <nav className="relative max-w-full">
            {/* Background with blur and gradient */}
            <div className="absolute inset-0 bg-[#16181D]/90 backdrop-blur-xl rounded-full border border-[#2C2F36] shadow-2xl shadow-[#0057FF]/10" />
            
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#0057FF]/5 via-[#00C3FF]/5 to-[#0057FF]/5 rounded-full blur-xl" />

            {/* Navigation items */}
            <div className="relative flex items-center gap-0.5 sm:gap-1 px-2 sm:px-3 py-2 sm:py-2.5">
              {navItems.map((item, index) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;

                return (
                  <motion.button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`relative px-2.5 sm:px-4 py-2 sm:py-3 rounded-full transition-all duration-300 min-w-[48px] min-h-[48px] flex items-center justify-center ${
                      isActive ? "text-white" : "text-[#8A8D92] hover:text-[#B0B3B8]"
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    aria-label={`Navigate to ${item.label}`}
                    aria-current={isActive ? "page" : undefined}
                  >
                    {/* Active background */}
                    {isActive && (
                      <motion.div
                        layoutId="activeNav"
                        className="absolute inset-0 bg-gradient-to-r from-[#0057FF] to-[#00C3FF] rounded-full shadow-lg shadow-[#0057FF]/50"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}

                    {/* Icon and label */}
                    <div className="relative z-10 flex flex-col items-center gap-0.5 sm:gap-1">
                      <Icon className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={isActive ? 2.5 : 2} />
                      <span className="text-[8px] sm:text-[10px] font-medium whitespace-nowrap hidden xs:block">
                        {item.label}
                      </span>
                    </div>

                    {/* Hover glow */}
                    {!isActive && (
                      <motion.div
                        className="absolute inset-0 bg-primary/10 rounded-full opacity-0"
                        whileHover={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      />
                    )}
                  </motion.button>
                );
              })}
            </div>

            {/* Bottom indicator dots (optional decorative element) - hidden on mobile */}
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 hidden sm:flex gap-1">
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  className="w-1 h-1 rounded-full bg-primary/30"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.3, 0.6, 0.3],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.2,
                  }}
                />
              ))}
            </div>
          </nav>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BottomNav;
